import { useQuery } from '@tanstack/react-query';
import { Download, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiGet, getToken } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { Money } from '@/components/ui/Money';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatusPill } from '@/components/ui/Pill';
import { ErrorState } from '@/components/ui/States';
import { DataTable } from '@/components/ui/Table';
import type { BillRow, Paginated } from '@/types/api';
import { fmtDate, fmtPhone } from '@/utils/format';
import { useDebounced } from '@/utils/useDebounced';

/**
 * Bills list. Server-side search + status filter — both stay in URL
 * params via useState so deep-linking the search results works (CAs
 * paste the URL into a chat). Pagination uses Laravel's `current_page`
 * meta from the paginate helper.
 */
export function BillsListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search, 300);

  const query = useQuery({
    queryKey: ['bills', { search: debouncedSearch, status, page }],
    queryFn: () =>
      apiGet<Paginated<BillRow>>(endpoints.bills, {
        search: debouncedSearch || undefined,
        status: status || undefined,
        page,
      }),
  });

  const rows = query.data?.items ?? [];
  const meta = query.data?.meta;

  return (
    <>
      <PageHeader
        title="Bills"
        description="Every invoice raised at any branch."
        actions={<ExportCsvButton />}
      />

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-3 mb-4">
        <Input
          name="search"
          placeholder="Search by bill number, customer name or phone…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          iconLeft={<Search className="h-4 w-4" />}
        />
        <Select
          name="status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="partial">Partial</option>
          <option value="due">Due</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
        </Select>
      </div>

      {query.isError ? (
        <ErrorState
          title="Couldn't load bills"
          description={(query.error as Error)?.message}
          onRetry={() => query.refetch()}
        />
      ) : (
        <DataTable
          rowKey={(r) => r.id}
          loading={query.isLoading}
          rows={rows}
          onRowClick={(b) => navigate(`/bills/${b.id}`)}
          empty={
            debouncedSearch || status
              ? 'No bills match those filters.'
              : 'No bills yet.'
          }
          columns={[
            {
              key: 'bill_number',
              header: 'Bill no.',
              cell: (b) => (
                <span className="font-mono text-xs">{b.bill_number}</span>
              ),
            },
            {
              key: 'customer',
              header: 'Customer',
              cell: (b) => (
                <div className="min-w-0">
                  <div className="font-medium truncate">
                    {b.customer?.name ||
                      b.customer_name_snapshot ||
                      'Walk-in customer'}
                  </div>
                  <div className="text-xs text-ink-soft">
                    {fmtPhone(b.customer?.phone || b.customer_phone_snapshot)}
                  </div>
                </div>
              ),
            },
            {
              key: 'date',
              header: 'Date',
              hideOnMobile: true,
              cell: (b) => (
                <span className="text-ink-soft">{fmtDate(b.created_at)}</span>
              ),
            },
            {
              key: 'type',
              header: 'Type',
              hideOnMobile: true,
              cell: (b) => (b.bill_type === 'gst' ? 'GST' : 'Non-GST'),
            },
            {
              key: 'status',
              header: 'Status',
              cell: (b) => <StatusPill status={b.status} />,
            },
            {
              key: 'total',
              header: 'Total',
              className: 'text-right',
              cell: (b) => <Money value={b.total} className="font-semibold" />,
            },
          ]}
        />
      )}

      {meta && meta.last_page > 1 && (
        <Pagination
          current={meta.current_page}
          last={meta.last_page}
          onPick={setPage}
        />
      )}
    </>
  );
}

function Pagination({
  current,
  last,
  onPick,
}: {
  current: number;
  last: number;
  onPick: (p: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mt-4 text-sm">
      <span className="text-ink-soft">
        Page {current} of {last}
      </span>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="outline"
          disabled={current <= 1}
          onClick={() => onPick(current - 1)}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={current >= last}
          onClick={() => onPick(current + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function ExportCsvButton() {
  // The export endpoint streams CSV — opening it in a new tab triggers
  // the browser's download. We append the bearer as a query param so
  // the request is auth'd without us having to set a header on a
  // window.open.
  const onExport = () => {
    const token = getToken() ?? '';
    const url = `${endpoints.reportExport}?type=bills&period=this_month&token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener');
  };
  return (
    <Button variant="outline" iconLeft={<Download className="h-4 w-4" />} onClick={onExport}>
      Export CSV
    </Button>
  );
}
